import {
  Component,
  OnInit,
  signal,
  computed
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RoleDto, PermissionDto, RoleUserDto, UpdateRoleDto, CreateRoleDto } from 'app/models/role';
import { RolesService } from 'app/services/role/role-service';
import { AuthService } from 'app/core/auth/auth.service';
import { PermissionDefinitions } from 'app/core/auth/permission-definitions';


@Component({
  selector: 'app-roles',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.scss']
})
export class RolesComponent implements OnInit {

  // ============================================================
  // Permission Definitions (Exposed to HTML)
  // ============================================================

  readonly Perm = PermissionDefinitions;

  // ============================================================
  // State - Roles
  // ============================================================

  roles = signal<RoleDto[]>([]);
  isLoading = signal(false);
  saving = signal(false);

  // ============================================================
  // Pagination
  // ============================================================

  currentPage = signal(1);
  itemsPerPage = signal(10);
  totalItems = signal(0);
  totalPages = signal(0);

  // ============================================================
  // Search
  // ============================================================

  searchTerm = signal('');

  // ============================================================
  // Create / Edit Modal
  // ============================================================

  showRoleModal = signal(false);
  roleFormMode = signal<'create' | 'edit'>('create');
  editingRoleId = signal<number | null>(null);
  roleForm: FormGroup;

  // ============================================================
  // Permissions Modal
  // ============================================================

  showPermissionsModal = signal(false);
  selectedRoleForPermissions = signal<RoleDto | null>(null);
  allPermissions = signal<PermissionDto[]>([]);
  selectedPermissionIds = signal<number[]>([]);
  permissionsSearch = signal('');
  permissionsLoading = signal(false);

  // ============================================================
  // Users Modal
  // ============================================================

  showUsersModal = signal(false);
  selectedRoleForUsers = signal<RoleDto | null>(null);
  roleUsers = signal<RoleUserDto[]>([]);
  usersPageNumber = signal(1);
  usersPageSize = signal(10);
  usersTotal = signal(0);
  usersTotalPages = signal(0);
  usersSearch = signal('');
  usersLoading = signal(false);

  // ============================================================
  // Delete Modal
  // ============================================================

  showDeleteModal = signal(false);
  pendingDeleteRole = signal<RoleDto | null>(null);

  // ============================================================
  // Computed
  // ============================================================

  startIndex = computed(() => {
    const total = this.totalItems();
    return total === 0 ? 0 : (this.currentPage() - 1) * this.itemsPerPage() + 1;
  });

  endIndex = computed(() => {
    const end = this.currentPage() * this.itemsPerPage();
    return Math.min(end, this.totalItems());
  });

  isEditMode = computed(() => this.roleFormMode() === 'edit');

  modalTitle = computed(() =>
    this.isEditMode() ? 'Edit Role' : 'Create New Role'
  );

  /**
   * ✅ هل اليوزر عنده صلاحية عرض الـ Roles؟
   */
  canViewRoles = computed(() => this.auth.hasPermission(this.Perm.Roles.View));

  // ============================================================
  // Grouped Permissions
  // ============================================================

  groupedPermissions = computed(() => {
    const groups: { [key: string]: PermissionDto[] } = {};
    const search = this.permissionsSearch().trim().toLowerCase();

    this.allPermissions()
      .filter(p => {
        if (!search) return true;
        return (
          p.name.toLowerCase().includes(search) ||
          (p.description || '').toLowerCase().includes(search)
        );
      })
      .forEach(p => {
        const module = p.name.split('.')[0] || 'Other';
        if (!groups[module]) groups[module] = [];
        groups[module].push(p);
      });

    return Object.keys(groups)
      .sort()
      .map(module => ({
        module,
        items: groups[module],
        selectedCount: groups[module].filter(p =>
          this.selectedPermissionIds().includes(p.id)
        ).length,
        totalCount: groups[module].length
      }));
  });

  // ============================================================
  // Constructor
  // ============================================================

  constructor(
    private roleService: RolesService,
    private fb: FormBuilder,
    public auth: AuthService
  ) {
    this.roleForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]]
    });
  }

  // ============================================================
  // Init
  // ============================================================

  ngOnInit(): void {
    this.printTokenInfo();

    // ✅ حمّل الـ Roles بس لو عنده صلاحية
    if (this.canViewRoles()) {
      this.loadPage(1);
    }
  }

  // ============================================================
  // Load Roles
  // ============================================================

  loadPage(page: number): void {
    // ✅ حماية إضافية
    if (!this.canViewRoles()) return;

    if (this.isLoading()) return;

    this.isLoading.set(true);
    this.currentPage.set(page);

    this.roleService.getAll({
      pageNumber: page,
      pageSize: this.itemsPerPage(),
      search: this.searchTerm()
    }).subscribe({
      next: (res) => {
        const data = res?.data;

        this.roles.set(data?.items ?? []);
        this.totalItems.set(data?.total_count ?? 0);
        this.totalPages.set(
          data?.total_pages ??
          Math.ceil((data?.total_count ?? 0) / this.itemsPerPage())
        );

        this.isLoading.set(false);

        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      error: (err) => {
        console.error('Error loading roles:', err);
        this.roles.set([]);
        this.totalItems.set(0);
        this.totalPages.set(0);
        this.isLoading.set(false);
      }
    });
  }

  // ============================================================
  // Search
  // ============================================================

  onSearch(event: Event): void {
    const term = ((event.target as HTMLInputElement).value || '').trim();
    this.searchTerm.set(term);
    this.currentPage.set(1);
    this.loadPage(1);
  }

  clearSearch(): void {
    if (!this.searchTerm()) return;
    this.searchTerm.set('');
    this.currentPage.set(1);
    this.loadPage(1);
  }

  // ============================================================
  // Pagination
  // ============================================================

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.currentPage()) {
      return;
    }
    this.loadPage(page);
  }

  previousPage(): void {
    if (this.currentPage() <= 1) return;
    this.loadPage(this.currentPage() - 1);
  }

  nextPage(): void {
    if (this.currentPage() >= this.totalPages()) return;
    this.loadPage(this.currentPage() + 1);
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    const total = this.totalPages();

    if (total <= 1) return total === 1 ? [1] : [];

    let startPage = Math.max(
      1,
      this.currentPage() - Math.floor(maxVisiblePages / 2)
    );

    let endPage = Math.min(total, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  onItemsPerPageChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    if (value === this.itemsPerPage()) return;

    this.itemsPerPage.set(value);
    this.currentPage.set(1);
    this.loadPage(1);
  }

  // ============================================================
  // Open Create Modal
  // ============================================================

  openCreateModal(): void {
    if (!this.auth.hasPermission(this.Perm.Roles.Create)) {
      alert('⛔ You do not have permission to create roles');
      return;
    }

    this.roleFormMode.set('create');
    this.editingRoleId.set(null);
    this.roleForm.reset({ name: '', description: '' });
    this.showRoleModal.set(true);
  }

  // ============================================================
  // Open Edit Modal
  // ============================================================

  openEditModal(role: RoleDto): void {
    if (!this.auth.hasPermission(this.Perm.Roles.Update)) {
      alert('⛔ You do not have permission to update roles');
      return;
    }

    this.roleFormMode.set('edit');
    this.editingRoleId.set(role.id);
    this.roleForm.patchValue({
      name: role.name,
      description: role.description ?? ''
    });
    this.showRoleModal.set(true);
  }

  // ============================================================
  // Close Role Modal
  // ============================================================

  closeRoleModal(): void {
    if (this.saving()) return;
    this.showRoleModal.set(false);
    this.roleForm.reset();
    this.editingRoleId.set(null);
  }

  // ============================================================
  // Submit Role (Create or Update)
  // ============================================================

  submitRole(): void {
    if (this.roleForm.invalid) {
      this.roleForm.markAllAsTouched();
      return;
    }

    const formValue = this.roleForm.value;
    this.saving.set(true);

    if (this.isEditMode()) {
      const id = this.editingRoleId();
      if (!id) {
        this.saving.set(false);
        return;
      }

      const dto: UpdateRoleDto = {
        name: formValue.name.trim(),
        description: formValue.description?.trim() || undefined
      };

      this.roleService.update(id, dto).subscribe({
        next: (res) => {
          this.saving.set(false);
          if (res.success) {
            this.showRoleModal.set(false);
            this.roleForm.reset();
            this.editingRoleId.set(null);
            this.loadPage(this.currentPage());
          }
        },
        error: (err) => {
          console.error('Update role error:', err);
          alert(err?.error?.message || 'Failed to update role');
          this.saving.set(false);
        }
      });
    } else {
      const dto: CreateRoleDto = {
        name: formValue.name.trim(),
        description: formValue.description?.trim() || undefined,
        permissionIds: []
      };

      this.roleService.create(dto).subscribe({
        next: (res) => {
          this.saving.set(false);
          if (res.success) {
            this.showRoleModal.set(false);
            this.roleForm.reset();
            this.loadPage(1);
          }
        },
        error: (err) => {
          console.error('Create role error:', err);
          alert(err?.error?.message || 'Failed to create role');
          this.saving.set(false);
        }
      });
    }
  }

  // ============================================================
  // Delete Role
  // ============================================================

  confirmDelete(role: RoleDto): void {
    if (!this.auth.hasPermission(this.Perm.Roles.Delete)) {
      alert('⛔ You do not have permission to delete roles');
      return;
    }

    this.pendingDeleteRole.set(role);
    this.showDeleteModal.set(true);
  }

  closeDeleteModal(): void {
    this.showDeleteModal.set(false);
    this.pendingDeleteRole.set(null);
  }

  deleteRole(): void {
    const role = this.pendingDeleteRole();
    if (!role) return;

    this.saving.set(true);

    this.roleService.delete(role.id).subscribe({
      next: (res) => {
        this.saving.set(false);
        if (res.success) {
          this.closeDeleteModal();
          this.loadPage(this.currentPage());
        }
      },
      error: (err) => {
        console.error('Delete role error:', err);
        alert(err?.error?.message || 'Failed to delete role');
        this.saving.set(false);
      }
    });
  }

  // ============================================================
  // Open Permissions Modal
  // ============================================================

  openPermissionsModal(role: RoleDto): void {
    if (!this.auth.hasPermission(this.Perm.Roles.ViewPermission)) {
      alert('⛔ You do not have permission to view role permissions');
      return;
    }

    this.selectedRoleForPermissions.set(role);
    this.permissionsSearch.set('');
    this.selectedPermissionIds.set([]);
    this.permissionsLoading.set(true);
    this.showPermissionsModal.set(true);

    this.roleService.getAllPermissions({ pageNumber: 1, pageSize: 1000 }).subscribe({
      next: (res) => {
        this.allPermissions.set(res?.data?.items ?? []);
      }
    });

    this.roleService.getRolePermissions(role.id, { pageNumber: 1, pageSize: 1000 }).subscribe({
      next: (res) => {
        const ids = (res?.data?.items ?? []).map(p => p.id);
        this.selectedPermissionIds.set(ids);
        this.permissionsLoading.set(false);
      },
      error: () => {
        this.permissionsLoading.set(false);
      }
    });
  }

  // ============================================================
  // Close Permissions Modal
  // ============================================================

  closePermissionsModal(): void {
    if (this.saving()) return;
    this.showPermissionsModal.set(false);
    this.selectedRoleForPermissions.set(null);
    this.selectedPermissionIds.set([]);
    this.permissionsSearch.set('');
  }

  // ============================================================
  // Permission Toggle
  // ============================================================

  togglePermission(id: number): void {
    const current = this.selectedPermissionIds();
    if (current.includes(id)) {
      this.selectedPermissionIds.set(current.filter(x => x !== id));
    } else {
      this.selectedPermissionIds.set([...current, id]);
    }
  }

  isPermissionSelected(id: number): boolean {
    return this.selectedPermissionIds().includes(id);
  }

  // ============================================================
  // Select / Deselect Group
  // ============================================================

  toggleGroup(items: PermissionDto[]): void {
    const current = [...this.selectedPermissionIds()];
    const allSelected = items.every(p => current.includes(p.id));

    if (allSelected) {
      const idsToRemove = items.map(p => p.id);
      this.selectedPermissionIds.set(
        current.filter(id => !idsToRemove.includes(id))
      );
    } else {
      const idsToAdd = items
        .filter(p => !current.includes(p.id))
        .map(p => p.id);
      this.selectedPermissionIds.set([...current, ...idsToAdd]);
    }
  }

  isGroupFullySelected(items: PermissionDto[]): boolean {
    return items.length > 0 && items.every(p => this.selectedPermissionIds().includes(p.id));
  }

  // ============================================================
  // Save Permissions
  // ============================================================

  savePermissions(): void {
    if (!this.auth.hasPermission(this.Perm.Roles.AssignPermission)) {
      alert('⛔ You do not have permission to assign permissions');
      return;
    }

    const role = this.selectedRoleForPermissions();
    if (!role) return;

    this.saving.set(true);

    this.roleService.syncPermissions(role.id, {
      permissionIds: this.selectedPermissionIds()
    }).subscribe({
      next: (res) => {
        this.saving.set(false);
        if (res.success) {
          this.closePermissionsModal();
          this.loadPage(this.currentPage());
        }
      },
      error: (err) => {
        console.error('Sync permissions error:', err);
        alert(err?.error?.message || 'Failed to save permissions');
        this.saving.set(false);
      }
    });
  }

  // ============================================================
  // Open Users Modal
  // ============================================================

  openUsersModal(role: RoleDto): void {
    if (!this.auth.hasPermission(this.Perm.Roles.View)) {
      alert('⛔ You do not have permission to view role users');
      return;
    }

    this.selectedRoleForUsers.set(role);
    this.usersPageNumber.set(1);
    this.usersSearch.set('');
    this.showUsersModal.set(true);
    this.loadRoleUsers(1);
  }

  loadRoleUsers(page: number): void {
    const role = this.selectedRoleForUsers();
    if (!role) return;

    this.usersLoading.set(true);
    this.usersPageNumber.set(page);

    this.roleService.getRoleUsers(role.id, {
      pageNumber: page,
      pageSize: this.usersPageSize(),
      search: this.usersSearch()
    }).subscribe({
      next: (res) => {
        const data = res?.data;

        this.roleUsers.set(data?.items ?? []);
        this.usersTotal.set(data?.total_count ?? 0);
        this.usersTotalPages.set(data?.total_pages ?? 0);

        this.usersLoading.set(false);
      },
      error: () => {
        this.usersLoading.set(false);
      }
    });
  }

  closeUsersModal(): void {
    this.showUsersModal.set(false);
    this.selectedRoleForUsers.set(null);
    this.roleUsers.set([]);
  }

  onUsersSearch(event: Event): void {
    const term = ((event.target as HTMLInputElement).value || '').trim();
    this.usersSearch.set(term);
    this.loadRoleUsers(1);
  }

  usersPreviousPage(): void {
    if (this.usersPageNumber() <= 1) return;
    this.loadRoleUsers(this.usersPageNumber() - 1);
  }

  usersNextPage(): void {
    if (this.usersPageNumber() >= this.usersTotalPages()) return;
    this.loadRoleUsers(this.usersPageNumber() + 1);
  }

  // ============================================================
  // Permission Helpers (for HTML)
  // ============================================================

  canCreateRole(): boolean {
    return this.auth.hasPermission(this.Perm.Roles.Create);
  }

  canUpdateRole(): boolean {
    return this.auth.hasPermission(this.Perm.Roles.Update);
  }

  canDeleteRole(): boolean {
    return this.auth.hasPermission(this.Perm.Roles.Delete);
  }

  canViewPermissions(): boolean {
    return this.auth.hasPermission(this.Perm.Roles.ViewPermission);
  }

  canAssignPermissions(): boolean {
    return this.auth.hasPermission(this.Perm.Roles.AssignPermission);
  }

  canViewRoleUsers(): boolean {
    return this.auth.hasPermission(this.Perm.Roles.View);
  }

  // ============================================================
  // Debug — Print Token Info
  // ============================================================

  printTokenInfo(): void {
    const payload = this.auth.getPayload();

    console.log(
      '%c🔍 ============== TOKEN INSPECT ==============',
      'color: #6366f1; font-weight: bold; font-size: 13px;'
    );

    console.log('%c📦 Full Payload:', 'color: #6366f1; font-weight: bold;', payload);

    console.log('%c👤 User Info:', 'color: #2563eb; font-weight: bold;');
    console.log('   ├─ ID:', this.auth.userId);
    console.log('   ├─ Name:', this.auth.userName);
    console.log('   ├─ Email:', this.auth.userEmail);
    console.log('   └─ Phone:', this.auth.userPhone);

    const roles = this.auth.getRoles();
    console.log(
      `%c🎭 Roles (${roles.length}):`,
      'color: #7c3aed; font-weight: bold;',
      roles
    );

    const permissions = this.auth.getPermissions();
    console.log(
      `%c🔐 Permissions (${permissions.length}):`,
      'color: #059669; font-weight: bold;',
      permissions
    );

    console.log('%c✅ Quick Checks:', 'color: #dc2626; font-weight: bold;');
    console.log('   ├─ Can View Roles?', this.canViewRoles());
    console.log('   ├─ Can Create Roles?', this.canCreateRole());
    console.log('   ├─ Can Update Roles?', this.canUpdateRole());
    console.log('   ├─ Can Delete Roles?', this.canDeleteRole());
    console.log('   ├─ Can View Permissions?', this.canViewPermissions());
    console.log('   ├─ Can Assign Permissions?', this.canAssignPermissions());
    console.log('   └─ Can View Users?', this.canViewRoleUsers());

    console.log(
      '%c================================================',
      'color: #6366f1; font-weight: bold; font-size: 13px;'
    );
  }

  // ============================================================
  // Helpers
  // ============================================================

  getSelectedPermissionsCount(): number {
    return this.selectedPermissionIds().length;
  }

  isAdminRole(role: RoleDto | null): boolean {
    return role?.name?.toLowerCase() === 'admin';
  }
}